import { useState, useEffect, useCallback } from 'react';
import { Download, Share2, Star, Link2, X, FileText } from 'lucide-react';
import { documenti as documentiApi } from '../../utils/api';

const CARD_SHADOW = '0 6px 18px rgba(35,38,47,0.08)';
const tint = (c, pct) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;

var styles = {
  panel: {
    width: '320px', minWidth: '320px',
    borderLeft: '0.5px solid var(--ds-border)',
    background: 'var(--ds-bg)',
    display: 'flex', flexDirection: 'column',
    height: '100%', overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: '0.5px solid var(--ds-border)',
  },
  headerTitle: {
    fontSize: '13px', fontWeight: 500,
    fontFamily: 'var(--font-display)',
    color: 'var(--ink)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    flex: 1,
  },
  closeBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    color: 'var(--ds-muted)', padding: '4px',
    display: 'flex', alignItems: 'center',
  },
  body: {
    flex: 1, overflowY: 'auto', padding: '16px',
    display: 'flex', flexDirection: 'column', gap: '16px',
  },
  iconBox: {
    width: '64px', height: '64px', borderRadius: 'var(--radius-card)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: tint('var(--brand)', 10),
    margin: '0 auto',
  },
  metaGroup: {
    display: 'flex', flexDirection: 'column', gap: '8px',
  },
  metaRow: {
    display: 'flex', justifyContent: 'space-between',
    fontSize: '12px',
  },
  metaLabel: { color: 'var(--ds-muted)', fontWeight: 500 },
  metaValue: { color: 'var(--ds-text)', fontWeight: 400, textAlign: 'right' },
  tagList: {
    display: 'flex', flexWrap: 'wrap', gap: '4px',
  },
  tag: {
    fontSize: '10px', padding: '2px 8px', borderRadius: '10px',
    background: tint('var(--brand)', 10), color: 'var(--brand)',
    fontWeight: 500,
  },
  actions: {
    display: 'flex', flexDirection: 'column', gap: '6px',
    padding: '16px', borderTop: '0.5px solid var(--ds-border)',
  },
  actionBtn: {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '8px 12px', borderRadius: 'var(--radius-base)', cursor: 'pointer',
    border: '0.5px solid var(--ds-border)',
    background: 'var(--ds-bg)',
    fontSize: '12px', fontWeight: 500,
    color: 'var(--ds-text)',
    transition: 'background 150ms',
  },
  actionBtnPreferito: {
    color: 'var(--warn)',
    borderColor: 'var(--warn)',
  },
};

function StarIcon(props) {
  var filled = props.filled;
  return (
    <Star
      width="14" height="14" strokeWidth={2}
      fill={filled ? 'var(--warn)' : 'none'}
      stroke={filled ? 'var(--warn)' : 'currentColor'}
    />
  );
}

var TIPO_LABELS = {
  preventivo: 'Preventivo', contratto: 'Contratto', fattura: 'Fattura',
  foto: 'Foto', ddt: 'DDT', bolla: 'Bolla', certificato: 'Certificato',
  collaudo: 'Collaudo', planimetria: 'Planimetria', sal: 'SAL',
  computo: 'Computo metrico', corrispondenza: 'Corrispondenza',
  pdf_generico: 'PDF generico', altro: 'Altro',
};

var PROVENIENZA_LABELS = {
  manuale: 'Caricato manualmente', whatsapp: 'Da WhatsApp',
  email: 'Da email', ai_generato: 'Generato da AI',
};

function LinkIcon() {
  return <Link2 width="12" height="12" strokeWidth={2} />;
}

function RevokeIcon() {
  return <X width="12" height="12" strokeWidth={2} />;
}

export default function PannelloDettaglio(props) {
  var documento = props.documento;
  var onChiudi = props.onChiudi;
  var onTogglePreferito = props.onTogglePreferito;
  var isPreferito = props.isPreferito;
  var onApriCondividi = props.onApriCondividi;

  var [links, setLinks] = useState([]);
  var [linksLoading, setLinksLoading] = useState(false);

  var fetchLinks = useCallback(function() {
    if (!documento) return;
    setLinksLoading(true);
    documentiApi.condivisioni(documento.id)
      .then(function(res) { setLinks(res.data || []); })
      .catch(function() { setLinks([]); })
      .finally(function() { setLinksLoading(false); });
  }, [documento]);

  useEffect(function() {
    fetchLinks();
  }, [fetchLinks]);

  if (!documento) return null;

  var handleDownload = async function() {
    // FE-074 — file su storage: apri l'URL diretto (comportamento storico).
    // Altrimenti (fattura/preventivo senza PDF allegato) scarica via endpoint
    // autenticato che genera il PDF al volo, come blob.
    if (documento.file_url) {
      window.open(documento.file_url, '_blank');
      return;
    }
    if (!documento.scaricabile) return;
    try {
      var res = await documentiApi.download(documento.id);
      var url = window.URL.createObjectURL(res.data);
      var a = document.createElement('a');
      a.href = url;
      a.download = (documento.nome_file || 'documento') + '.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // no-op: il bottone resta, l'utente puo' riprovare
    }
  };

  var handleShare = function() {
    if (onApriCondividi) {
      onApriCondividi(documento);
    }
  };

  var handleRevoca = function(token) {
    if (!window.confirm('Vuoi revocare questo link di condivisione?')) return;
    documentiApi.revocaLink(token)
      .then(function() { fetchLinks(); })
      .catch(function() {});
  };

  var dataUpload = '';
  if (documento.data_upload) {
    var d = new Date(documento.data_upload);
    dataUpload = d.toLocaleDateString('it-IT') + ' ' + d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  }

  var fmtScadenza = function(dt) {
    if (!dt) return '';
    return new Date(dt).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
  };

  var prefBtnStyle = Object.assign({}, styles.actionBtn, isPreferito ? styles.actionBtnPreferito : {});

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <div style={styles.headerTitle}>{documento.nome_file}</div>
        <button style={styles.closeBtn} onClick={onChiudi}>
          <X width="16" height="16" strokeWidth={2} />
        </button>
      </div>

      <div style={styles.body}>
        <div style={styles.iconBox}>
          <FileText width="28" height="28" strokeWidth={1.5} stroke="var(--brand)" />
        </div>

        <div style={styles.metaGroup}>
          <div style={styles.metaRow}>
            <span style={styles.metaLabel}>Tipo</span>
            <span style={styles.metaValue}>{TIPO_LABELS[documento.tipo] || documento.tipo}</span>
          </div>
          {documento.cliente_nome && (
            <div style={styles.metaRow}>
              <span style={styles.metaLabel}>Cliente</span>
              <span style={styles.metaValue}>{documento.cliente_nome}</span>
            </div>
          )}
          {documento.cantiere_nome && (
            <div style={styles.metaRow}>
              <span style={styles.metaLabel}>Cantiere</span>
              <span style={styles.metaValue}>{documento.cantiere_nome}</span>
            </div>
          )}
          {documento.fornitore_nome && (
            <div style={styles.metaRow}>
              <span style={styles.metaLabel}>Fornitore</span>
              <span style={styles.metaValue}>{documento.fornitore_nome}</span>
            </div>
          )}
          {documento.cartella_nome && (
            <div style={styles.metaRow}>
              <span style={styles.metaLabel}>Cartella</span>
              <span style={styles.metaValue}>{documento.cartella_nome}</span>
            </div>
          )}
          <div style={styles.metaRow}>
            <span style={styles.metaLabel}>Caricato</span>
            <span style={styles.metaValue}>{dataUpload}</span>
          </div>
          <div style={styles.metaRow}>
            <span style={styles.metaLabel}>Dimensione</span>
            <span style={styles.metaValue}>{documento.dimensione_leggibile || '-'}</span>
          </div>
          <div style={styles.metaRow}>
            <span style={styles.metaLabel}>Provenienza</span>
            <span style={styles.metaValue}>{PROVENIENZA_LABELS[documento.provenienza] || documento.provenienza}</span>
          </div>
        </div>

        {documento.tags && documento.tags.length > 0 && (
          <div>
            <div style={Object.assign({}, styles.metaLabel, { marginBottom: '6px' })}>Tag</div>
            <div style={styles.tagList}>
              {documento.tags.map(function(t) {
                var tagStyle = Object.assign({}, styles.tag);
                if (t.colore) {
                  tagStyle = Object.assign({}, tagStyle, {
                    background: t.colore + '1a',
                    color: t.colore,
                  });
                }
                return <span key={t.id} style={tagStyle}>{t.nome}</span>;
              })}
            </div>
          </div>
        )}

        {/* Sezione link condivisione attivi */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--ds-muted)' }}>Link condivisione</span>
            <button
              onClick={handleShare}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 500, color: 'var(--brand)', padding: '4px 0', minHeight: 44, display: 'flex', alignItems: 'center' }}
            >+ Nuovo link</button>
          </div>
          {linksLoading ? (
            <div style={{ fontSize: 11, color: 'var(--ds-muted)' }}>Caricamento...</div>
          ) : links.length === 0 ? (
            <div style={{ fontSize: 11, color: 'var(--ds-muted)' }}>Nessun link attivo</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {links.map(function(lnk) {
                var attivo = lnk.is_valido;
                return (
                  <div key={lnk.id} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 8px', borderRadius: 'var(--radius-base)',
                    background: 'var(--chip)',
                    fontSize: 11,
                  }}>
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                      background: attivo ? 'var(--brand)' : 'var(--ds-danger)',
                    }} />
                    <span style={{ flex: 1, color: 'var(--ds-text)', fontWeight: 400 }}>
                      <LinkIcon /> scade {fmtScadenza(lnk.expires_at)} &middot; {lnk.aperture} apert.
                    </span>
                    {attivo && (
                      <button
                        onClick={function() { handleRevoca(lnk.token); }}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--ds-danger)', fontSize: 11, fontWeight: 500,
                          padding: '2px 4px', minWidth: 44, minHeight: 44,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                        title="Revoca"
                      >
                        <RevokeIcon />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div style={styles.actions}>
        <button
          style={styles.actionBtn}
          onClick={handleDownload}
          onMouseEnter={function(e) { e.currentTarget.style.background = 'var(--chip)'; }}
          onMouseLeave={function(e) { e.currentTarget.style.background = 'var(--ds-bg)'; }}
        >
          <Download width="14" height="14" strokeWidth={2} /> Scarica
        </button>
        <button
          style={styles.actionBtn}
          onClick={handleShare}
          onMouseEnter={function(e) { e.currentTarget.style.background = 'var(--chip)'; }}
          onMouseLeave={function(e) { e.currentTarget.style.background = 'var(--ds-bg)'; }}
        >
          <Share2 width="14" height="14" strokeWidth={2} /> Condividi link
        </button>
        <button
          style={prefBtnStyle}
          onClick={function() { onTogglePreferito(documento.id, !!isPreferito); }}
          onMouseEnter={function(e) { e.currentTarget.style.background = 'var(--chip)'; }}
          onMouseLeave={function(e) { e.currentTarget.style.background = 'var(--ds-bg)'; }}
        >
          <StarIcon filled={isPreferito} />
          {isPreferito ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
        </button>
      </div>
    </div>
  );
}
